import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';
import { bloqcibeApi } from '../../store/slices/apiSlice';
import CustomTablePagination from '../../components/common/TablePagination';
import AnimatedBloqcubeLogo from '../../components/Logo/AnimatedBloqcubeLogo';
import { CustomTableHead, CustomTableHeadCell } from '../../components/@extended/CustomTable';
import { getPriorityColor, getStatusColor } from '../util';
import CustomButton from '../../components/@extended/CustomButton';

const QueryListDialog = ({ open, onClose, sponsorId, trialId, siteId, trialSiteId, stepKey, sectionKey, subSectionKey, subjectMasterId, crfMasterId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [getQueries] = bloqcibeApi.endpoints.getQueries.useLazyQuery();
  const [exportQueries] = bloqcibeApi.endpoints.exportQueries.useMutation();
  const [filteredQueries, setFilteredQueries] = useState([]); // Store filtered queries
  const [allQueries, setAllQueries] = useState([]); // Store all fetched queries
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('excel');
  const [reportType, setReportType] = useState('regular');
  const [filters, setFilters] = useState({
    status: [],
    priority: [],
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 5,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    if (open && sponsorId && trialId && siteId) {
      fetchQueries();
    }
  }, [open, sponsorId, trialId, siteId]);

  const applyFiltersToQueries = useCallback((queriesToFilter) => {
    let filtered = [...queriesToFilter];

    // Apply status filter
    if (filters.status.length > 0) {
      filtered = filtered.filter((query) => filters.status.includes(query.status));
    }

    // Apply priority filter
    if (filters.priority.length > 0) {
      filtered = filtered.filter((query) => filters.priority.includes(query.priority));
    }

    setFilteredQueries(filtered);
    
    // Update pagination based on filtered results
    setPagination((prev) => ({
      ...prev,
      page: 0,
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / prev.rowsPerPage),
    }));
  }, [filters]);

  // Filter queries when filters change
  useEffect(() => {
    if (allQueries.length > 0) {
      applyFiltersToQueries(allQueries);
    }
  }, [filters, allQueries, applyFiltersToQueries]);

  const fetchQueries = async () => {
    if (!sponsorId || !trialId || !siteId) return;

    setIsLoading(true);
    try {
      // Fetch all queries (without pagination) for filtering
      const result = await getQueries({
        sponsorId,
        trialId,
        siteId,
        // stepKey,
        // sectionKey,
        // subSectionKey,
        subjectMasterId,
        crfMasterId,
        page: 1,
        limit: 100, // Fetch a large number to get all queries
        active: true,
      });

      if (result.data) {
        const fetchedQueries = result.data.data || [];
        setAllQueries(fetchedQueries);
        // Apply filters to the fetched queries
        applyFiltersToQueries(fetchedQueries);
      }
    } catch (error) {
      console.error('Error fetching queries:', error);
      setFilteredQueries([]);
      setAllQueries([]);
    } finally {
      setIsLoading(false);
    }
  };


  // Get paginated queries
  const paginatedQueries = useMemo(() => {
    const startIndex = pagination.page * pagination.rowsPerPage;
    const endIndex = startIndex + pagination.rowsPerPage;
    return filteredQueries.slice(startIndex, endIndex);
  }, [filteredQueries, pagination.page, pagination.rowsPerPage]);

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage,
      rowsPerPage: newRowsPerPage || prev.rowsPerPage,
    }));
  };


  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const handleRemoveFilter = (filterType, filterValue) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: prev[filterType].filter((val) => val !== filterValue),
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      status: [],
      priority: [],
    });
  };

  const hasActiveFilters = filters.status.length > 0 || filters.priority.length > 0;

  const statusOptions = ['OPEN', 'RESOLVED', 'VERIFIED', 'REOPENED'];
  const priorityOptions = ['LOW', 'MEDIUM', 'HIGH'];

  const handleExport = async () => {
    if (!sponsorId || !trialId || !siteId) {
      console.error('Missing required parameters for export');
      return;
    }

    setIsExporting(true);
    try {
      const exportPayload = {
        format: exportFormat,
        reportType: exportFormat === 'excel' ? reportType : undefined, // Only include reportType for Excel
        sponsorId: String(sponsorId),
        trialId: String(trialId),
        stepKey,
        sectionKey,
        subSectionKey,
        subjectMasterId,
        crfMasterId,
        siteId: siteId.toString(),
        status: filters.status.length > 0 ? filters.status : undefined,
        priority: filters.priority.length > 0 ? filters.priority : undefined,
        active: true,
      };

      const blob = await exportQueries(exportPayload).unwrap();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFormat === 'excel' 
        ? (reportType === 'detailed' 
          ? `queries_detailed_report_${Date.now()}.xlsx`
          : `queries_export_${Date.now()}.xlsx`)
        : `queries_export_${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting queries:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleNavigateToQuery = (query, event) => {
    // Prevent navigation if clicking on the action button
    // if (event && event.target.closest('button')) {
    //   return;
    // }

    if (!query.subjectMasterId || !trialSiteId) {
      console.error('Missing required navigation data:', { subjectMasterId: query.subjectMasterId, trialSiteId });
      return;
    }

    const params = new URLSearchParams();
    params.set('fieldKey', query.fieldKey);
    params.set('queryId', query.id);
    params.set('tab', 'query');

    let targetPath = '';

    if (query.crfMasterId && query.stepKey) {
      // CRF visit detail route
      params.set('sectionKey', query.sectionKey || '');
      if (query.subSectionKey) {
        params.set('subSectionKey', query.subSectionKey);
      }
      targetPath = `/eConcent/${trialId}/trial-site/${trialSiteId}/site/${siteId}/subject/${query.subjectMasterId}/crf/${query.crfMasterId}/${query.stepKey}`;
    } else {
      // Subject details route
      if (query.stepKey) params.set('stepKey', query.stepKey);
      if (query.sectionKey) params.set('sectionKey', query.sectionKey);
      if (query.subSectionKey) params.set('subSectionKey', query.subSectionKey);
      targetPath = `/eConcent/${trialId}/trial-site/${trialSiteId}/site/${siteId}/subject/${query.subjectMasterId}/details`;
    }

    const search = `?${params.toString()}`;

    if (location.pathname === targetPath) {
      // Already on the required path – only update query params without pushing new history entry
      navigate({ pathname: targetPath, search }, { replace: true });
    } else {
      // Navigate to the required path with params (push new history entry)
      navigate(`${targetPath}${search}`);
    }

    onClose(); // Close the dialog after navigation
  };

  const columns = useMemo(() => [
    {
      id: 'title',
      label: 'Title',
      minWidth: 200,
      maxWidth: 250,
      align: 'left',
    },
    {
      id: 'description',
      label: 'Description',
      minWidth: 200,
      maxWidth: 300,
      align: 'left',
    },
    {
      id: 'subjectNumber',
      label: 'Subject',
      minWidth: 120,
      maxWidth: 150,
      align: 'left',
    },
    {
      id: 'fieldKey',
      label: 'Field',
      minWidth: 120,
      maxWidth: 180,
      align: 'left',
    },
     {
      id: 'assignees',
      label: 'Assignees',
      minWidth: 150,
      maxWidth: 150,
      align: 'left',
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      maxWidth: 120,
      align: 'center',
    },
    {
      id: 'priority',
      label: 'Priority',
      minWidth: 100,
      maxWidth: 120,
      align: 'center',
    },
    {
      id: 'createdBy',
      label: 'Created By',
      minWidth: 150,
      maxWidth: 150,
      align: 'left',
    },
    {
      id: 'action',
      label: 'Action',
      minWidth: 100,
      maxWidth: 120,
      align: 'center',
    },
  ], []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6">Queries</Typography>
          {stepKey && sectionKey && (
            <Typography variant="subtitle2" color="text.secondary">
              Showing queries for selected form.
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
            }}
          >
            <Box sx={{ height: 250, width: 250 }}>
              <AnimatedBloqcubeLogo />
            </Box>
          </Box>
        ) : (
          <>
            {/* Filter Section */}
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.grey[50],
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filter by Status</InputLabel>
                    <Select
                      multiple
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      label="Filter by Status"
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return <em>All Statuses</em>;
                        }
                        return selected.length === statusOptions.length
                          ? 'All Statuses'
                          : `${selected.length} selected`;
                      }}
                    >
                      {statusOptions.map((status) => {
                        const colors = getStatusColor(status);
                        const statusLabel =
                          status === 'OPEN'
                            ? 'Open'
                            : status === 'RESOLVED'
                            ? 'Resolved'
                            : status === 'VERIFIED'
                            ? 'Verified'
                            : status === 'REOPENED'
                            ? 'Reopened'
                            : status;
                        return (
                          <MenuItem key={status} value={status}>
                            <Chip
                              label={statusLabel}
                              size="small"
                              sx={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                fontWeight: 500,
                              }}
                            />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filter by Priority</InputLabel>
                    <Select
                      multiple
                      value={filters.priority}
                      onChange={(e) => handleFilterChange('priority', e.target.value)}
                      label="Filter by Priority"
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return <em>All Priorities</em>;
                        }
                        return selected.length === priorityOptions.length
                          ? 'All Priorities'
                          : `${selected.length} selected`;
                      }}
                    >
                      {priorityOptions.map((priority) => {
                        const colors = getPriorityColor(priority);
                        const priorityLabel =
                          priority === 'LOW'
                            ? 'Low'
                            : priority === 'MEDIUM'
                            ? 'Medium'
                            : priority === 'HIGH'
                            ? 'High'
                            : priority;
                        return (
                          <MenuItem key={priority} value={priority}>
                            <Chip
                              label={priorityLabel}
                              size="small"
                              sx={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                fontWeight: 500,
                              }}
                            />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={() => setExportDialogOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Export
                  </Button>
                </Grid>
              </Grid>

              {/* Active Filters Chips */}
              {hasActiveFilters && (
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <FilterListIcon sx={{ fontSize: '1rem', color: 'text.secondary', mr: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    Active Filters:
                  </Typography>
                  {filters.status.map((status) => {
                    const colors = getStatusColor(status);
                    const statusLabel =
                      status === 'OPEN'
                        ? 'Open'
                        : status === 'RESOLVED'
                        ? 'Resolved'
                        : status === 'VERIFIED'
                        ? 'Verified'
                        : status === 'REOPENED'
                        ? 'Reopened'
                        : status;
                    return (
                      <Chip
                        key={`status-${status}`}
                        label={`Status: ${statusLabel}`}
                        size="small"
                        onDelete={() => handleRemoveFilter('status', status)}
                        sx={{
                          backgroundColor: colors.bg,
                          color: colors.text,
                          fontWeight: 500,
                        }}
                      />
                    );
                  })}
                  {filters.priority.map((priority) => {
                    const colors = getPriorityColor(priority);
                    const priorityLabel =
                      priority === 'LOW'
                        ? 'Low'
                        : priority === 'MEDIUM'
                        ? 'Medium'
                        : priority === 'HIGH'
                        ? 'High'
                        : priority;
                    return (
                      <Chip
                        key={`priority-${priority}`}
                        label={`Priority: ${priorityLabel}`}
                        size="small"
                        onDelete={() => handleRemoveFilter('priority', priority)}
                        sx={{
                          backgroundColor: colors.bg,
                          color: colors.text,
                          fontWeight: 500,
                        }}
                      />
                    );
                  })}
                  <Button
                    size="small"
                    onClick={clearAllFilters}
                    sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Clear All
                  </Button>
                </Box>
              )}
            </Box>

            {filteredQueries.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 300,
                }}
              >
                <Typography variant="subtitle1" color="text.secondary">
                  {hasActiveFilters ? 'No queries match the selected filters' : 'No queries found'}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer
                  sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    border: `1px solid ${theme.palette.grey[100]}`,
                    // Hide scrollbar for Chrome, Safari and Opera
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    // Hide scrollbar for IE, Edge and Firefox
                    msOverflowStyle: 'none', // IE and Edge
                    scrollbarWidth: 'none', // Firefox
                  }}
                >
                  <Table stickyHeader>
                    <CustomTableHead>
                      {columns.map((column) => (
                        <CustomTableHeadCell
                          key={column.id}
                          align={column.align}
                          width={column.minWidth}
                          sx={{
                            maxWidth: column.maxWidth,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {column.id === 'createdBy' ? (
                            <Box sx={{ lineHeight: 1, m: 0, p: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: '1rem', mb: 0.2 }}>
                                Created By
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.7rem', lineHeight: '0.7rem', mt: 0 }}
                              >
                                Date
                              </Typography>
                            </Box>
                          ) : (
                            column.label
                          )}
                        </CustomTableHeadCell>
                      ))}
                    </CustomTableHead>
                    <TableBody>
                      {paginatedQueries.map((query) => {
                        const statusColor = getStatusColor(query.status);
                        const priorityColor = getPriorityColor(query.priority);

                        // Extract fieldName from metaData
                        let fieldName = query.fieldKey || '-';
                        let createdByName = query.createdBy || 'Unknown';
                        try {
                          if (query.metaData) {
                            const metaData =
                              typeof query.metaData === 'string' ? JSON.parse(query.metaData) : query.metaData;
                            if (metaData?.fieldName) {
                              fieldName = metaData?.fieldName;
                            }
                            if (metaData?.createdByName) {
                              createdByName = metaData?.createdByName;
                            }
                          }
                        } catch (error) {
                          console.error('Error parsing metaData:', error);
                        }

                        // Format title with tag
                        const titleWithTag = query.tag ? `[${query.tag}] ${query.title}` : query.title;

                        // Get assignee names
                        const assigneeNames =
                          query.assignees && query.assignees.length > 0
                            ? query.assignees.map((a) => a.name || a.email || a.userId).join(', ')
                            : '-';

                        return (
                          <TableRow
                            key={query.id}
                            hover
                            onClick={(e) => handleNavigateToQuery(query, e)}
                            sx={{
                              cursor: 'pointer',
                              '&:nth-of-type(even)': {
                                backgroundColor: theme.palette.grey[50],
                              },
                            }}
                          >
                            <TableCell align="left" sx={{ maxWidth: 250, overflow: 'hidden' }}>
                              <Tooltip title={titleWithTag} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                  }}
                                >
                                  {titleWithTag}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="left" sx={{ maxWidth: 300, overflow: 'hidden' }}>
                              <Tooltip title={query.description || '-'} arrow>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    maxWidth: '100%',
                                  }}
                                >
                                  {query.description || '-'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="left" sx={{ maxWidth: 150, overflow: 'hidden' }}>
                              <Tooltip title={query.subjectNumber || query.participantId || '-'} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                  }}
                                >
                                  {query.subjectNumber || query.participantId || '-'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="left" sx={{ maxWidth: 180, overflow: 'hidden' }}>
                              <Tooltip title={fieldName} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                  }}
                                >
                                  {fieldName}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="left" sx={{ maxWidth: 250, overflow: 'hidden' }}>
                              <Tooltip title={assigneeNames} arrow>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                  }}
                                >
                                  {assigneeNames}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={query.status}
                                size="small"
                                sx={{
                                  backgroundColor: statusColor.bg,
                                  color: statusColor.text,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={query.priority}
                                size="small"
                                sx={{
                                  backgroundColor: priorityColor.bg,
                                  color: priorityColor.text,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                }}
                              />
                            </TableCell>
                            <TableCell align="left" sx={{ maxWidth: 200, overflow: 'hidden' }}>
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography variant="body2">{createdByName}</Typography>
                                    <Typography variant="caption">
                                      {moment(query.createdAt).format('MM/DD/YYYY hh:mm a')}
                                    </Typography>
                                  </Box>
                                }
                                arrow
                              >
                                <Box sx={{ lineHeight: 1, m: 0, p: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      lineHeight: '1rem',
                                      mb: 0.2,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '100%',
                                    }}
                                  >
                                    {createdByName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: '0.7rem',
                                      lineHeight: '0.7rem',
                                      mt: 0,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '100%',
                                      display: 'block',
                                    }}
                                  >
                                    {moment(query.createdAt).format('MM/DD/YYYY hh:mm a')}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleNavigateToQuery(query, e);
                                }}
                                sx={{ textTransform: 'none' }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {filteredQueries.length > 0 && (
                  <Box
                    sx={{
                      borderTop: `1px solid ${theme.palette.divider}`,
                      flexShrink: 0,
                    }}
                  >
                    <CustomTablePagination
                      page={pagination.page}
                      rowsPerPage={pagination.rowsPerPage}
                      count={filteredQueries.length}
                      onNewPage={handlePageChange}
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      {/* Export Format Selection Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Queries</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>File Format</InputLabel>
              <Select
                value={exportFormat}
                onChange={(e) => {
                  setExportFormat(e.target.value);
                  // Reset report type to regular if CSV is selected
                  if (e.target.value === 'csv') {
                    setReportType('regular');
                  }
                }}
                label="File Format"
              >
                <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                <MenuItem value="csv">CSV (.csv)</MenuItem>
              </Select>
            </FormControl>
            {exportFormat === 'excel' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Report Type</InputLabel>
                <Select value={reportType} onChange={(e) => setReportType(e.target.value)} label="Report Type">
                  <MenuItem value="regular">Summary Report</MenuItem>
                  <MenuItem value="detailed">Detailed Report (with Query Logs)</MenuItem>
                </Select>
              </FormControl>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {exportFormat === 'excel'
                ? reportType === 'detailed'
                  ? 'Detailed report will include all queries in a single sheet with bordered sections. Each query section contains complete query details and all query logs with history.'
                  : 'Regular Excel export will include all columns matching the table view.'
                : 'CSV export will include current query statuses.'}
            </Typography>
            {hasActiveFilters && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="caption" color="info.dark">
                  Active filters will be applied to the export.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <CustomButton variant="outlined" onClick={() => setExportDialogOpen(false)}>
            Cancel
          </CustomButton>
          <CustomButton
            variant="contained"
            onClick={handleExport}
            disabled={isExporting}
            startIcon={<FileDownloadIcon />}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </CustomButton>
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default QueryListDialog;

